// The client half of the version gate (backend: `ClientVersion` + `ClientVersionGateMiddleware`).
//
// Every request to OUR API says what is asking — platform, the API compat number this SPA was built against, the
// SPA build, and on iOS the app binary's build — and a 426 from the API is turned into the one thing that fixes it:
// a reload on the web; in the app, a live update to the newest bundle (liveUpdate.ts) or, when the binary itself is
// too old for that, the App Store.
//
// It wraps `globalThis.fetch` rather than living in the openapi-fetch middleware because ~20 call sites fetch the
// API directly (media bytes, avatars, attachments, debug pages) and a header that rides only SOME requests is a
// gate an old bundle walks around. The wrap touches requests to the API origin alone: a custom header on a
// third-party request (a presigned S3 upload, a vendor CDN) would turn it into a CORS preflight that host refuses.
import { create } from "zustand";
import { getNativeBuild, getPlatform } from "@cntrl/native";
import { API_URL, BUILD_NUMBER } from "../config/env.js";

/**
 * The API compatibility number this SPA was built against. MUST equal the backend's `ClientVersion.ApiCompat`
 * (`ClientCompatParityTests` fails the build otherwise). Bump BOTH in the commit that breaks older clients.
 */
export const API_COMPAT = 3;

/**
 * The lowest iOS app build (CFBundleVersion) this SPA runs on. Raise it in the commit that makes the web code
 * depend on something native — a new plugin, a new method on one — to the build number of the App Store build that
 * ships it. A live-update bundle carries it in its manifest (scripts/live-update-bundle.mjs reads this line), and an
 * app below it keeps its current bundle instead of installing one that would call into nothing.
 */
export const MIN_NATIVE_BUILD = 0;

/**
 * This bundle's version: its commit's unix time, baked in at build (vite.config.ts). "0" under tests and on a build
 * made without git, which never live-updates. The web falls back to the lane's BUILD_NUMBER for diagnosis only.
 */
export const BUNDLE_VERSION: string = import.meta.env.VITE_BUNDLE_VERSION ?? "0";

const PLATFORM_HEADER = "X-Client-Platform";
const COMPAT_HEADER = "X-Client-Compat";
const BUNDLE_HEADER = "X-Client-Bundle";
const NATIVE_BUILD_HEADER = "X-Client-Native-Build";

/** The query param a gate-triggered reload carries, so a reload that lands on the SAME stale bundle stops looping. */
const RELOADED_PARAM = "cntrl_updated";

/**
 * What a refused client must do: `reload` (web — a reload already failed to fix it), `updating` (the app is fetching
 * the newest bundle and will reload into it), or `store` (only an App Store update can fix this binary).
 */
export type OutdatedRemedy = "reload" | "updating" | "store";

interface ClientOutdatedState {
  remedy: OutdatedRemedy | null;
}

/** Set once the API refuses this client. Deliberately NOT in the store-reset registry: no band switch fixes it. */
export const useClientOutdated = create<ClientOutdatedState>(() => ({ remedy: null }));

let installed = false;
let reloadedForUpdate = false;

/** True when `url` is a request to our own API — the only requests that carry the headers or can answer 426. */
export function isApiRequest(url: string, base: string = API_URL || window.location.origin): boolean {
  try {
    const target = new URL(url, window.location.href);
    return target.origin === new URL(base, window.location.href).origin && target.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

/** The headers this client sends. `nativeBuild` is null on web. */
export function clientHeaders(platform: string, nativeBuild: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    [PLATFORM_HEADER]: platform,
    [COMPAT_HEADER]: String(API_COMPAT),
    [BUNDLE_HEADER]: BUNDLE_VERSION !== "0" ? BUNDLE_VERSION : BUILD_NUMBER,
  };
  if (nativeBuild) headers[NATIVE_BUILD_HEADER] = nativeBuild;
  return headers;
}

/**
 * Decide what a 426 means for this client. `app-update-required` is always the App Store. `client-outdated` is a
 * reload on the web (unless this page IS the reload, in which case the reload did not help and the reader is
 * asked), and in the app a live update — which falls back to the App Store if the binary cannot take one.
 */
export function remedyFor(problemType: string, platform: string, alreadyReloaded: boolean): OutdatedRemedy | "reload-now" | null {
  if (problemType.endsWith("/app-update-required")) return "store";
  if (!problemType.endsWith("/client-outdated")) return null;
  if (platform !== "web") return "updating";
  return alreadyReloaded ? "reload" : "reload-now";
}

/** Reload into the newest SPA, marking the URL so a reload that does not help cannot loop. */
export function reloadForUpdate(): void {
  const url = new URL(window.location.href);
  url.searchParams.set(RELOADED_PARAM, "1");
  window.location.replace(url.toString());
}

async function onRefused(response: Response, platform: string): Promise<void> {
  if (useClientOutdated.getState().remedy) return;
  let type = "";
  try {
    const body = (await response.clone().json()) as { type?: unknown };
    type = typeof body.type === "string" ? body.type : "";
  } catch {
    return; // a 426 that is not our ProblemDetails is not the gate
  }

  const remedy = remedyFor(type, platform, reloadedForUpdate);
  if (remedy === "reload-now") {
    reloadForUpdate();
  } else if (remedy === "updating") {
    useClientOutdated.setState({ remedy });
    // Imported lazily: liveUpdate reads this module's exports. On success the app reloads and this never returns.
    const { updateNow } = await import("./liveUpdate.js");
    if (!(await updateNow().catch(() => false))) useClientOutdated.setState({ remedy: "store" });
  } else if (remedy) {
    useClientOutdated.setState({ remedy });
  }
}

let nativeBuildPromise: Promise<string | null> | null = null;

/** The installed app's build number (null on web). One bridge call per page, shared. */
export function nativeBuild(): Promise<string | null> {
  nativeBuildPromise ??= getNativeBuild();
  return nativeBuildPromise;
}

/**
 * Install the fetch wrap. Call ONCE, at boot, before anything creates the API client (openapi-fetch and SignalR
 * both capture `fetch` when they are built). Idempotent.
 */
export function installClientVersion(): void {
  if (installed) return;
  installed = true;

  // A gate-triggered reload marks the URL; remember it for this page and take the marker back off.
  const here = new URL(window.location.href);
  if (here.searchParams.has(RELOADED_PARAM)) {
    reloadedForUpdate = true;
    here.searchParams.delete(RELOADED_PARAM);
    window.history.replaceState(window.history.state, "", here.toString());
  }

  const platform = getPlatform();
  // The native build is one bridge call; every API request waits on the same promise, so none goes out without it.
  const headers = nativeBuild().then((build) => clientHeaders(platform, build));
  const original = globalThis.fetch.bind(globalThis);

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = input instanceof Request ? input.url : input.toString();
    if (!isApiRequest(url)) return original(input, init);

    const extra = await headers;
    if (input instanceof Request && init === undefined) {
      // Mutate in place: rebuilding a Request with a body turns it into a streaming upload browsers refuse over
      // HTTP/1.1 (the orgLens lesson). A Request's headers are mutable for every mode we send.
      for (const [name, value] of Object.entries(extra)) input.headers.set(name, value);
      const response = await original(input);
      if (response.status === 426) await onRefused(response, platform);
      return response;
    }

    const merged = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    for (const [name, value] of Object.entries(extra)) merged.set(name, value);
    const response = await original(input, { ...init, headers: merged });
    if (response.status === 426) await onRefused(response, platform);
    return response;
  };
}
